import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { CategoryTag } from '../../components/CategoryTag';
import { SeletorDeCategoria } from '../../components/SeletorDeCategoria';
import {
  ApiError,
  api,
  type Aprendizado,
  type CategoryNode,
  type DataSource,
  type Transaction,
} from '../../lib/api';

/**
 * Categoria, recebedor e direcao, editaveis na propria linha.
 *
 * Sem modal e sem tela de edicao: corrigir a categoria de um lancamento e
 * uma acao de um segundo, e abrir um formulario para isso faria ninguem
 * corrigir nada. O campo vira editavel onde ele ja esta.
 *
 * Abaixo de cada valor aparece de ONDE ele veio. Essa e a metade que torna o
 * "nunca deixar em branco" honesto: uma categoria que a instituicao informou
 * e uma que caiu na gaveta "Outros" ocupam a mesma coluna e valem coisas
 * muito diferentes na hora de confiar num relatorio. Quem preencheu fica
 * dito, em letra miuda, sem competir com o dado.
 */

const ORIGEM: Record<DataSource, { texto: string; firme: boolean }> = {
  BANK: { texto: 'informado pelo banco', firme: true },
  RULE: { texto: 'por uma regra sua', firme: true },
  // A IA classifica sozinha depois de cada varredura. Fraca de proposito:
  // e leitura de uma descricao, nao informacao de origem nem escolha de
  // ninguem — e a tela tem que deixar isso a vista para ser corrigida.
  AI: { texto: 'a IA leu a descricao', firme: false },
  USER: { texto: 'voce escolheu', firme: true },
  LEARNED: { texto: 'aprendido do seu historico', firme: true },
  DERIVED: { texto: 'deduzido da descricao', firme: false },
  DEFAULT: { texto: 'ninguem classificou ainda', firme: false },
};

export function Origem({
  source,
  nota = null,
}: {
  source: DataSource | null;
  nota?: string | null;
}) {
  if (!source) return null;
  const { texto, firme } = ORIGEM[source];

  // Quando foi a IA, o que ela entendeu vale mais que dizer que foi ela: a
  // frase e justamente o que permite discordar com um clique em vez de abrir
  // o extrato para descobrir do que se trata.
  const conteudo = source === 'AI' && nota ? `IA: ${nota}` : texto;

  return (
    <div className={`origem${firme ? '' : ' fraca'}`} title={source === 'AI' ? texto : undefined}>
      {conteudo}
    </div>
  );
}

export function CelulaCategoria({
  transaction,
  categorias,
}: {
  transaction: Transaction;
  categorias: CategoryNode[];
}) {
  const cliente = useQueryClient();
  const [editando, setEditando] = useState(false);

  const [aprendeu, setAprendeu] = useState<Aprendizado | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const salvar = useMutation({
    mutationFn: ({
      categoryId,
      kind,
    }: {
      categoryId: string;
      kind: 'INCOME' | 'EXPENSE' | 'TRANSFER';
    }) =>
      api.updateTransaction(transaction.id, {
        categoryId,
        /**
         * O lado vem da CATEGORIA escolhida, e nao do que estava la.
         *
         * Escolher "Supermercado" numa linha marcada como receita e a forma
         * mais direta de dizer que a classificacao estava errada; o servidor
         * ajusta o sinal junto, pelo mesmo caminho do botao de direcao.
         *
         * Rotulo de transferencia nao manda lado nenhum: ali o servidor tira
         * o lancamento das somas sem mexer no valor.
         */
        ...(kind !== 'TRANSFER' && kind !== transaction.kind ? { kind } : {}),
      }),
    onSuccess: (resposta) => {
      setEditando(false);
      setErro(null);
      setAprendeu(resposta.aprendizado);
      invalidar(cliente);
    },
    // O servidor recusa a conversao de um pagamento de fatura, e com razao:
    // desfazer ali contaria o gasto do cartao duas vezes. Recusar em silencio
    // seria pior — o clique pareceria funcionar e nada mudaria.
    onError: (falha) => {
      setEditando(false);
      setErro(falha instanceof ApiError ? falha.message : 'Nao consegui salvar.');
    },
  });

  if (transaction.splits.length > 0) {
    return <span className="badge">dividido em {transaction.splits.length}</span>;
  }

  if (!editando) {
    const transferencia = transaction.kind === 'TRANSFER';

    return (
      <>
        <button
          type="button"
          className="rosca-item"
          style={{ gridTemplateColumns: '1fr', padding: 2 }}
          onClick={() => setEditando(true)}
          title={
            transferencia
              ? 'Clique para escolher uma categoria. Isto tira o lancamento das transferencias.'
              : 'Clique para trocar a categoria'
          }
        >
          <span>
            {transferencia && !transaction.categoryName ? (
              <span className="badge">transferencia</span>
            ) : (
              <CategoryTag
                name={transaction.categoryName}
                colorToken={transaction.colorToken}
                title={transaction.categoryPath ?? undefined}
              />
            )}
            <Origem source={transaction.categorySource} nota={transaction.categoryNote} />
          </span>
        </button>
        {erro && <div className="origem expense">{erro}</div>}
        {aprendeu && (
          <AvisoDeAprendizado aprendizado={aprendeu} aoFechar={() => setAprendeu(null)} />
        )}
      </>
    );
  }

  return (
    <SeletorDeCategoria
      categorias={categorias}
      /**
       * Transferencia nao tem lado proprio, entao o lado vem do SINAL.
       *
       * Oferecer a lista errada seria pior que nao oferecer nada: a pessoa
       * escolheria "Salario" para uma saida e o servidor recusaria, sem ela
       * entender por que.
       */
      kind={ladoDoLancamento(transaction)}
      valor={transaction.categoryId}
      aoEscolher={(categoryId, kind) => salvar.mutate({ categoryId, kind })}
      aoDesistir={() => setEditando(false)}
    />
  );
}

/**
 * Que grupo abre a lista. Nenhum e escondido.
 *
 * Numa transferencia o sinal do valor nao responde a pergunta — "Pagamento
 * recebido" de +400 num cartao e a fatura diminuindo, nao uma receita —,
 * entao os rotulos de transferencia vem primeiro. Nos demais, o proprio lado
 * do lancamento abre a lista, porque e o acerto mais provavel.
 */
function ladoDoLancamento(transaction: Transaction): 'INCOME' | 'EXPENSE' | 'TRANSFER' | null {
  if (transaction.kind === 'INCOME') return 'INCOME';
  if (transaction.kind === 'EXPENSE') return 'EXPENSE';
  return 'TRANSFER';
}

/**
 * O que o sistema aprendeu com a escolha que acabou de ser feita.
 *
 * Existe por uma razao so: quando uma correcao muda outros doze lancamentos,
 * a pessoa precisa saber na hora. Aplicar em silencio e o pior dos mundos —
 * ela so descobriria meses depois, ao estranhar um numero no relatorio, sem
 * nenhuma pista do que o mudou.
 */
function AvisoDeAprendizado({
  aprendizado,
  aoFechar,
}: {
  aprendizado: Aprendizado;
  aoFechar: () => void;
}) {
  return (
    <div className="aprendeu">
      <span>
        Anotado: <strong>{aprendizado.chave}</strong> agora vai para{' '}
        <strong>{aprendizado.categoryName}</strong> sozinho.
        {aprendizado.aplicados > 0 && (
          <>
            {' '}
            Ja apliquei em {aprendizado.aplicados}{' '}
            {aprendizado.aplicados === 1 ? 'lancamento parecido' : 'lancamentos parecidos'}.
          </>
        )}
      </span>
      <button className="btn ghost" onClick={aoFechar} aria-label="Fechar aviso">
        ok
      </button>
    </div>
  );
}

export function CelulaRecebedor({ transaction }: { transaction: Transaction }) {
  const cliente = useQueryClient();
  const [editando, setEditando] = useState(false);

  const salvar = useMutation({
    mutationFn: (counterpartyName: string) =>
      api.updateTransaction(transaction.id, { counterpartyName }),
    onSuccess: () => {
      setEditando(false);
      invalidar(cliente);
    },
  });

  if (editando) {
    return (
      <input
        className="edit-inline"
        autoFocus
        defaultValue={transaction.counterpartyName ?? ''}
        disabled={salvar.isPending}
        maxLength={160}
        onBlur={(evento) => {
          const valor = evento.target.value.trim();
          if (valor && valor !== transaction.counterpartyName) salvar.mutate(valor);
          else setEditando(false);
        }}
        onKeyDown={(evento) => {
          if (evento.key === 'Enter') evento.currentTarget.blur();
          if (evento.key === 'Escape') setEditando(false);
        }}
      />
    );
  }

  return (
    <button
      type="button"
      className="rosca-item"
      style={{ gridTemplateColumns: '1fr', padding: 2 }}
      onClick={() => setEditando(true)}
      title="Clique para corrigir quem recebeu"
    >
      <span className="payee-name">
        <span>{transaction.counterpartyName ?? '—'}</span>
        {(transaction.paymentMethod ?? transaction.counterpartyDocument) && (
          <span className="payee-meta">
            {[transaction.paymentMethod, transaction.counterpartyDocument]
              .filter(Boolean)
              .join(' · ')}
          </span>
        )}
        <Origem source={transaction.counterpartySource} />
      </span>
    </button>
  );
}

/**
 * Trocar saida por entrada.
 *
 * O servidor inverte o sinal junto, e nao so o rotulo: um valor de -80
 * marcado como entrada sairia do gasto do mes E nao entraria na receita —
 * sumiria duas vezes, sem avisar.
 */
export function BotaoDirecao({ transaction }: { transaction: Transaction }) {
  const cliente = useQueryClient();

  const trocar = useMutation({
    mutationFn: () =>
      api.updateTransaction(transaction.id, {
        kind: transaction.kind === 'INCOME' ? 'EXPENSE' : 'INCOME',
        // A categoria antiga e do outro tipo e o servidor a recusaria;
        // limpar deixa o preenchimento automatico decidir de novo.
        categoryId: null,
      }),
    onSuccess: () => invalidar(cliente),
  });

  if (transaction.kind === 'TRANSFER') return null;

  return (
    <button
      className="btn ghost"
      disabled={trocar.isPending}
      onClick={() => trocar.mutate()}
      title={
        transaction.kind === 'INCOME'
          ? 'Marcar como saida: o valor passa a contar como gasto.'
          : 'Marcar como entrada: o valor passa a contar como receita.'
      }
    >
      {transaction.kind === 'INCOME' ? 'E saida' : 'E entrada'}
    </button>
  );
}

/**
 * "Isto se repete todo mes."
 *
 * O caminho que faltava entre as duas telas: quem ve o aluguel sair no
 * extrato ja sabe que ele sai todo mes, e ate aqui precisava ir a outra aba e
 * redigitar nome, valor, dia e categoria — com chance de errar em cada um.
 *
 * O que vai junto, e e o que faz valer a pena, e o nome de quem recebeu: e ele
 * que faz a baixa dos proximos meses acontecer sozinha, porque o valor da
 * conta de luz muda todo mes e o nome da concessionaria nao.
 *
 * A serie comeca no MES SEGUINTE. Este lancamento ja aconteceu; um vencimento
 * em aberto para ele faria o "o que falta pagar" mentir no mesmo instante.
 */
export function BotaoFixar({
  transaction,
  jaEhFixo,
}: {
  transaction: Transaction;
  jaEhFixo: boolean;
}) {
  const cliente = useQueryClient();
  const [recado, setRecado] = useState<string | null>(null);

  const fixar = useMutation({
    mutationFn: () => api.fixarLancamento(transaction.id),
    onSuccess: (resultado) => {
      setRecado(
        resultado.jaExistia
          ? 'Ja era fixo'
          : resultado.recurrence.kind === 'INCOME'
            ? 'Ganho fixo criado'
            : 'Despesa fixa criada',
      );
      invalidar(cliente);
      void cliente.invalidateQueries({ queryKey: ['recurrences'] });
      void cliente.invalidateQueries({ queryKey: ['payables'] });
      void cliente.invalidateQueries({ queryKey: ['compromissos'] });
    },
    onError: (falha) => {
      setRecado(falha instanceof ApiError ? falha.message : 'Nao consegui.');
    },
  });

  // Transferencia entre contas proprias nao e despesa nem ganho — nao ha
  // serie a criar, e o botao nao deve sugerir que ha.
  if (transaction.kind === 'TRANSFER') return null;

  if (recado) return <span className="muted fixo-recado">{recado}</span>;

  if (jaEhFixo) {
    return (
      <span className="badge" title="Ja existe uma serie mensal para este recebedor">
        fixo
      </span>
    );
  }

  const entrada = transaction.kind === 'INCOME';

  return (
    <button
      className="btn ghost"
      disabled={fixar.isPending}
      onClick={() => fixar.mutate()}
      title={
        entrada
          ? 'Isto entra todo mes: criar ganho fixo a partir do mes que vem, e reconhecer sozinho quando cair.'
          : 'Isto sai todo mes: criar despesa fixa a partir do mes que vem, e dar baixa sozinha quando sair.'
      }
    >
      {fixar.isPending ? '...' : entrada ? 'Ganho fixo' : 'Despesa fixa'}
    </button>
  );
}

function invalidar(cliente: ReturnType<typeof useQueryClient>): void {
  void cliente.invalidateQueries({ queryKey: ['transactions'] });
  void cliente.invalidateQueries({ queryKey: ['dashboard'] });
  void cliente.invalidateQueries({ queryKey: ['payees'] });
  // O aprendizado cria regra: a tela de Regras nao pode ficar desatualizada.
  void cliente.invalidateQueries({ queryKey: ['rules'] });
}
